import { useState } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  LayoutDashboard, School, LogOut, Shield, Menu, X,
  Settings, ChevronRight, Receipt, FileText, Bell,
} from 'lucide-react';
import { useSuperAdminStore } from '../stores/superAdminStore';
import { useUIStore } from '../stores/uiStore';
import { cn } from '../lib/utils';
import LanguageToggle from '../components/LanguageToggle';

const navItems = [
  { path: '/super-admin/dashboard', labelKey: 'superAdmin.nav.dashboard', icon: LayoutDashboard },
  { path: '/super-admin/tenants', labelKey: 'superAdmin.nav.schools', icon: School },
  { path: '/super-admin/billing', labelKey: 'superAdmin.nav.billing', icon: Receipt },
  { path: '/super-admin/alerts', labelKey: 'superAdmin.nav.alerts', icon: Bell },
  { path: '/super-admin/logs', labelKey: 'superAdmin.nav.logs', icon: FileText },
  { path: '/super-admin/settings', labelKey: 'superAdmin.nav.settings', icon: Settings },
];

export default function SuperAdminLayout() {
  const { t } = useTranslation();
  const direction = useUIStore((s) => s.direction);
  const logout = useSuperAdminStore((s) => s.logout);
  const user = useSuperAdminStore((s) => s.user);
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/super-admin/login');
  };

  // Page title from current route
  const currentNav = navItems.find((item) => location.pathname.startsWith(item.path));
  const pageTitle = currentNav ? t(currentNav.labelKey) : '';

  const sidebar = (
    <div className="flex flex-col h-full">
      {/* Brand */}
      <div className="px-5 py-5 border-b border-indigo-500/10">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-600 to-indigo-700 flex items-center justify-center shadow-lg shadow-indigo-500/25">
            <Shield size={18} className="text-white" />
          </div>
          <div>
            <p className="text-sm font-bold text-slate-800">{t('appName')}</p>
            <p className="text-[11px] text-indigo-500 font-semibold tracking-wider uppercase">{t('superAdmin.nav.superAdmin')}</p>
          </div>
        </div>
      </div>

      {/* Nav */}
      <nav className="flex-1 px-3 py-5 space-y-1">
        {navItems.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            end={item.path.endsWith('dashboard')}
            onClick={() => setMobileOpen(false)}
            className={({ isActive }) =>
              cn(
                'group flex items-center gap-3 px-3 py-2.5 rounded-xl text-[13px] font-medium transition-all duration-200',
                isActive
                  ? 'bg-indigo-50 text-indigo-700 shadow-sm shadow-indigo-100/50'
                  : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
              )
            }
          >
            {({ isActive }) => (
              <>
                <div className={cn(
                  'w-8 h-8 rounded-lg flex items-center justify-center transition-colors duration-200',
                  isActive ? 'bg-indigo-600 text-white shadow-sm' : 'bg-slate-100 text-slate-400 group-hover:bg-slate-200 group-hover:text-slate-500'
                )}>
                  <item.icon size={16} />
                </div>
                <span className="flex-1">{t(item.labelKey)}</span>
                {isActive && <ChevronRight size={14} className="text-indigo-400 rtl:rotate-180" />}
              </>
            )}
          </NavLink>
        ))}
      </nav>

      {/* User footer */}
      <div className="px-4 py-4 border-t border-slate-100">
        <div className="flex items-center gap-3">
          <div className="relative">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-indigo-500 to-indigo-600 flex items-center justify-center shadow-sm">
              <span className="text-xs font-bold text-white">
                {(user?.name || 'A').charAt(0).toUpperCase()}
              </span>
            </div>
            <div className="absolute -bottom-0.5 -end-0.5 w-3 h-3 rounded-full bg-emerald-400 border-2 border-white" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-slate-700 truncate">{user?.name || t('superAdmin.nav.admin')}</p>
            <p className="text-[11px] text-slate-400 truncate">{user?.email}</p>
          </div>
          <button
            onClick={handleLogout}
            className="p-2 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors duration-200 cursor-pointer"
            title={t('superAdmin.nav.logout')}
          >
            <LogOut size={16} />
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div dir={direction} className="min-h-screen bg-slate-50/80 flex">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-[260px] bg-white border-e border-slate-200/60 flex-col shrink-0 sticky top-0 h-screen">
        {sidebar}
      </aside>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-slate-900/30 backdrop-blur-sm" onClick={() => setMobileOpen(false)} />
          <aside className="absolute inset-y-0 start-0 w-[280px] bg-white shadow-2xl shadow-slate-900/10">
            <div className="absolute top-4 end-4">
              <button onClick={() => setMobileOpen(false)} className="p-2 rounded-lg text-slate-400 hover:bg-slate-100 cursor-pointer">
                <X size={18} />
              </button>
            </div>
            {sidebar}
          </aside>
        </div>
      )}

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top bar */}
        <header className="sticky top-0 z-30 bg-white/90 backdrop-blur-md border-b border-slate-200/60 px-4 lg:px-8 h-14 flex items-center gap-3">
          <button
            onClick={() => setMobileOpen(true)}
            className="lg:hidden p-2 -ms-2 rounded-lg text-slate-500 hover:bg-slate-100 cursor-pointer"
          >
            <Menu size={20} />
          </button>

          {/* Page title (desktop) */}
          <h2 className="hidden lg:block text-sm font-semibold text-slate-700">{pageTitle}</h2>

          <div className="flex-1" />

          <LanguageToggle />

          <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-50 border border-emerald-100">
            <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
            <span className="text-[11px] font-medium text-emerald-700">{t('superAdmin.nav.systemOnline')}</span>
          </div>
        </header>

        {/* Content */}
        <main className="flex-1 p-4 lg:p-8">
          <div className="max-w-[1200px] mx-auto">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
